// crm.repo — data CRM/Loyalty: pelanggan + belanja (LTV dari omzet) + penukaran poin.
import { supabase } from '@core/supabase';
import { fetchAllPaged } from '@core/paging';
import { rowToTicket, fetchRounds, MONEY_COLS, type TicketRow } from '@core/ticketMap';
import { omzetTiket, trxCount } from '@domain/money';

export interface CrmCustomer { id: number; nama: string; kat: string; wa: string | null; lahir: string | null; kota: string | null; outlet: string | null; waCount: number; waLast: string | null }
export interface Spend { ltv: number; trx: number; last: string | null
  /** Rincian tiap servis (untuk poin ber-masa-berlaku). */
  per: { no: string; tgl: string; nilai: number }[];
}

export async function listCustomers(): Promise<CrmCustomer[]> {
  const data = await fetchAllPaged<Record<string, unknown>>((dari, sampai) => supabase().from('customers')
    .select('id,nama,kat,wa,lahir,kota,outlet,wa_count,wa_last').order('nama').range(dari, sampai));
  return (data ?? []).map((r) => { const c = r as Record<string, unknown>;
    return { ...(c as unknown as CrmCustomer), waCount: (c['wa_count'] as number) ?? 0, waLast: (c['wa_last'] as string) ?? null }; });
}

/** Tandai N pelanggan sudah di-WA (counter+1, tgl hari ini). Atomik & scope-guarded di server (RPC). */
export async function tandaiWA(ids: number[]): Promise<number> {
  if (!ids.length) return 0;
  const { data, error } = await supabase().rpc('wa_tandai', { p_ids: ids });
  if (error) throw new Error(error.message);
  const r = data as { ok?: boolean; pesan?: string; n?: number } | null;
  if (!r?.ok) throw new Error(r?.pesan ?? 'Gagal menandai WA.');
  return r.n ?? 0;
}

/** Belanja per pelanggan (LTV = Σ omzet tiket Sudah Diambil, jml transaksi, kunjungan terakhir). */
export async function spendByCustomer(): Promise<Map<number, Spend>> {
  const rows = await fetchAllPaged<TicketRow & { customer_id: number }>((dari, sampai) => supabase().from('tickets')
    .select(`${MONEY_COLS},customer_id`).eq('status', 'Sudah Diambil').eq('archived', false)
    .order('no').range(dari, sampai));
  const rounds = await fetchRounds(rows.map((r) => r.no));
  const m = new Map<number, Spend>();
  for (const r of rows) {
    const t = rowToTicket(r, rounds.get(r.no) ?? []);
    const cur = m.get(r.customer_id) ?? { ltv: 0, trx: 0, last: null, per: [] };
    const nilai = omzetTiket(t);
    cur.ltv += nilai; cur.trx += trxCount(t); // per-SESI (servis-ulang bayar=+1, garansi gratis=0, refund=−1)
    // Rincian per servis dipakai poin ber-masa-berlaku: poin hanya bisa hangus kalau
    // diketahui kapan ia lahir (tanggal unit diambil).
    if (t.tglAmbilISO && nilai > 0) cur.per.push({ no: t.no, tgl: t.tglAmbilISO, nilai });
    if (t.tglAmbilISO && (!cur.last || t.tglAmbilISO > cur.last)) cur.last = t.tglAmbilISO;
    m.set(r.customer_id, cur);
  }
  return m;
}

/** Belanja SATU pelanggan (utk detail Pelanggan — lebih ringan dari scan semua). */
export async function spendForCustomer(id: number): Promise<Spend> {
  const { data, error } = await supabase().from('tickets')
    .select(`${MONEY_COLS}`).eq('customer_id', id).eq('status', 'Sudah Diambil').eq('archived', false);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as TicketRow[];
  const rounds = await fetchRounds(rows.map((r) => r.no));
  const s: Spend = { ltv: 0, trx: 0, last: null, per: [] };
  for (const r of rows) {
    const t = rowToTicket(r, rounds.get(r.no) ?? []);
    const nilai = omzetTiket(t);
    s.ltv += nilai; s.trx += trxCount(t);
    if (t.tglAmbilISO && nilai > 0) s.per.push({ no: t.no, tgl: t.tglAmbilISO, nilai });
    if (t.tglAmbilISO && (!s.last || t.tglAmbilISO > s.last)) s.last = t.tglAmbilISO;
  }
  return s;
}

export interface Redemption { points: number; note: string | null; by_user: string | null; created_at: string }
/** Riwayat penukaran poin SATU pelanggan (ledger). */
export async function redemptionsForCustomer(id: number): Promise<Redemption[]> {
  const { data, error } = await supabase().from('point_redemptions')
    .select('points,note,by_user,created_at').eq('customer_id', id).order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Redemption[];
}

export async function redeemedByCustomer(): Promise<Map<number, number>> {
  const data = await fetchAllPaged<{ customer_id: number; points: number }>((a, b) =>
    supabase().from('point_redemptions').select('customer_id,points').order('id').range(a, b));
  const m = new Map<number, number>();
  for (const r of data) m.set(r.customer_id, (m.get(r.customer_id) ?? 0) + r.points);
  return m;
}

export async function addRedemption(d: { customer_id: number; outlet: string | null; points: number; note: string; by_user: string }): Promise<void> {
  const { error } = await supabase().from('point_redemptions').insert(d); if (error) throw new Error(error.message);
}
